import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatErr } from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Search, Check, User, Building2 } from "@/components/icons";
import { useAuth } from "../../lib/auth";
import { useOfficeFilter } from "../../lib/officeFilter";
import DateTimePicker from "../../components/DateTimePicker";

export default function NewAppointment({ rolePath = "doctor" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { officeId } = useOfficeFilter();
  const [patients, setPatients] = useState([]);
  const [offices, setOffices] = useState([]);
  const [appts, setAppts] = useState([]);
  const [saving, setSaving] = useState(false);

  const [query, setQuery] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [selected, setSelected] = useState(null);
  const wrapRef = useRef(null);

  const [form, setForm] = useState({
    date: location.state?.prefillDate || "",
    duration_min: 30,
    reason: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const [p, o, a] = await Promise.all([
          api.get("/patients"),
          api.get("/offices").catch(() => ({ data: [] })),
          api.get("/appointments").catch(() => ({ data: [] })),
        ]);
        setPatients(p.data);
        setOffices(o.data);
        setAppts(a.data);
      } catch (err) {
        toast.error(formatErr(err.response?.data?.detail));
      }
    })();
  }, []);

  // Close suggestions on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target))
        setShowResults(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const norm = (s) => (s || "").toString().toLowerCase().trim();
  const results = useMemo(() => {
    const q = norm(query);
    if (!q) return patients.slice(0, 8);
    return patients
      .filter((p) => {
        const name = norm(p.full_name);
        const phone = norm(p.phone);
        const doc = norm(p.document);
        return (
          name.includes(q) ||
          phone.replace(/\s|-/g, "").includes(q.replace(/\s|-/g, "")) ||
          doc.includes(q)
        );
      })
      .slice(0, 12);
  }, [query, patients]);

  const pickPatient = (p) => {
    setSelected(p);
    setQuery(p.full_name);
    setShowResults(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) {
      toast.error("Selecciona un paciente");
      return;
    }
    if (!form.date) {
      toast.error("Selecciona fecha y hora");
      return;
    }
    setSaving(true);
    try {
      await api.post("/appointments", {
        patient_id: selected.id,
        office_id: officeId || null,
        date: form.date,
        duration_min: parseInt(form.duration_min) || 30,
        reason: form.reason,
        status: "scheduled",
      });
      toast.success("Cita agendada");
      // Pass appt date so agenda lands on the right day
      navigate(`/${rolePath}/agenda`, {
        state: { focusDate: form.date.slice(0, 10) },
      });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const initials = (name) =>
    (name || "")
      .split(" ")
      .map((s) => s[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase();

  return (
    <div className="space-y-5 fade-up" data-testid="new-appointment-page">
      <button
        onClick={() => navigate(`/${rolePath}/agenda`)}
        className="inline-flex items-center gap-2 text-[#1B405B] font-semibold text-sm hover:underline"
        data-testid="back-to-agenda"
      >
        <ArrowLeft className="w-4 h-4" /> Volver a la agenda
      </button>

      {/* Header */}
      <div>
        <h1 className="text-3xl font-extrabold text-[#163144] tracking-tight">
          Nueva cita
        </h1>
        <p className="text-sm text-[#1B405B]/70">
          Busca al paciente, elige fecha y motivo de la consulta.
        </p>
      </div>

      <form onSubmit={submit} className="grid lg:grid-cols-[1.2fr_1fr] gap-5">
        {/* Patient search */}
        <section className="glass rounded-[28px] p-6 space-y-4">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-[#1B405B]" />
            <h4 className="font-extrabold text-[#163144]">Paciente</h4>
            {selected && (
              <span className="ml-auto text-[10px] uppercase tracking-widest font-bold text-[#019A75] inline-flex items-center gap-1">
                <Check className="w-3 h-3" /> seleccionado
              </span>
            )}
          </div>

          <div className="relative" ref={wrapRef}>
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1B405B]/60" />
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSelected(null);
                setShowResults(true);
              }}
              onFocus={() => setShowResults(true)}
              placeholder="Buscar por nombre, teléfono o documento…"
              className="w-full glass-input rounded-xl pl-11 pr-4 py-3 text-sm"
              data-testid="patient-search-input"
              autoComplete="off"
            />

            {showResults && (
              <div
                className="absolute z-20 left-0 right-0 mt-2 max-h-72 overflow-auto rounded-2xl glass-strong shadow-xl"
                data-testid="patient-search-results"
              >
                {results.length === 0 && (
                  <div className="p-4 text-sm text-[#1B405B]/70">
                    Sin resultados. Crea el paciente primero desde "Pacientes".
                  </div>
                )}
                {results.map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => pickPatient(p)}
                    className="w-full text-left px-4 py-2.5 hover:bg-white/30 flex items-center gap-3 border-b border-white/30 last:border-0 transition"
                    data-testid={`patient-option-${p.id}`}
                  >
                    <div className="w-9 h-9 rounded-full bg-black text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {initials(p.full_name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-[#163144] text-sm truncate">
                        {p.full_name}
                      </div>
                      <div className="text-xs text-[#1B405B]/70 truncate">
                        {p.phone || "Sin teléfono"}
                        {p.document ? ` · ${p.document}` : ""}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {selected && (
            <div
              className="rounded-2xl bg-[#DBEAFE]/50 border border-white/70 p-4 flex items-center gap-3"
              data-testid="patient-selected-card"
            >
              <div className="w-12 h-12 rounded-full bg-[#01BC8E] text-white flex items-center justify-center text-sm font-bold">
                {initials(selected.full_name)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-extrabold text-[#163144] truncate">
                  {selected.full_name}
                </div>
                <div className="text-xs text-[#1B405B]/75 truncate">
                  {selected.phone || "Sin teléfono"}
                  {selected.email ? ` · ${selected.email}` : ""}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelected(null);
                  setQuery("");
                }}
                className="text-xs font-bold text-[#1B405B]/70 hover:text-[#163144]"
              >
                Cambiar
              </button>
            </div>
          )}
        </section>

        {/* Schedule details */}
        <section className="glass rounded-[28px] p-6 space-y-4">
          <h4 className="font-extrabold text-[#163144]">Detalles de la cita</h4>

          {/* Active office info (chosen from top bar) */}
          <div
            className="flex items-center gap-3 rounded-2xl bg-[#DBEAFE]/50 border border-white/70 p-3"
            data-testid="appt-active-office"
          >
            <div className="w-9 h-9 rounded-xl bg-black text-white flex items-center justify-center flex-shrink-0">
              <Building2 className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#1B405B]/65">
                Oficina
              </div>
              <div className="font-bold text-[#163144] text-sm truncate">
                {offices.find((o) => o.id === officeId)?.name ||
                  "Sin oficina · selecciona arriba para asignarla"}
              </div>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-[#1B405B]/65">
              Fecha y hora
            </label>
            <div className="mt-1">
              <DateTimePicker
                value={form.date}
                onChange={(v) => setForm({ ...form, date: v })}
                testid="appt-date"
                placeholder="Selecciona fecha y hora"
                busyRanges={appts
                  .filter((a) => a.status !== "cancelled")
                  .filter(
                    (a) => !officeId || !a.office_id || a.office_id === officeId
                  )
                  .map((a) => {
                    const start = parseApptDate(a.date);
                    if (!start) return null;
                    const end = new Date(
                      start.getTime() + (a.duration_min || 30) * 60000
                    );
                    return { start, end };
                  })
                  .filter(Boolean)}
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-[#1B405B]/65">
              Duración (min)
            </label>
            <input
              type="number"
              min="5"
              step="5"
              value={form.duration_min}
              onChange={(e) =>
                setForm({ ...form, duration_min: e.target.value })
              }
              className="w-full mt-1 glass-input rounded-xl px-4 py-2.5"
              data-testid="appt-duration-input"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-[#1B405B]/65">
              Motivo de la consulta
            </label>
            <textarea
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="Ej: Control general, dolor abdominal..."
              className="w-full mt-1 glass-input rounded-xl px-4 py-2.5 min-h-[80px] text-sm"
              data-testid="appt-reason-input"
            />
          </div>
        </section>

        <div className="lg:col-span-2 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => navigate(`/${rolePath}/agenda`)}
            className="px-6 py-2.5 rounded-full font-bold text-[#1B405B] bg-white/60 backdrop-blur-md border border-white/70 hover:bg-white"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="appt-submit-button"
            className="btn-primary"
          >
            {saving ? "Guardando..." : "Agendar cita"}
          </button>
        </div>
      </form>
    </div>
  );
}


function parseApptDate(s) {
  if (!s || typeof s !== "string") return null;
  // Accept "YYYY-MM-DDTHH:mm" (local) or full ISO with timezone
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return null;
  return new Date(
    parseInt(m[1]),
    parseInt(m[2]) - 1,
    parseInt(m[3]),
    parseInt(m[4]),
    parseInt(m[5])
  );
}
